import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  sendUserAnswersAction,
  setAnswersAction,
} from 'root-redux/actions/user'
import {
  selectIsUpgradePurchased,
  selectUUID,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useCompatibleRetentionFlowPaymentMethod } from 'hooks/useCompatibleRetentionFlowPaymentMethod'

import { getSubscriptionUIStatus } from 'helpers/getSubscriptionUIStatus'

import { Events, eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { Language, READABLE_APP_NAMES, UnsubscribeReason } from 'root-constants'

import { FlowButton } from '../../components/FlowButton'
import { FlowFeedbackPopup } from '../../components/FlowFeedbackPopup'
import { FlowOption } from '../../components/FlowOption'
import { FlowPageContainer } from '../../components/FlowPageContainer'
import { useCancelSubscription } from '../../hooks/useCancelSubscription'
import { useRetentionFlowV2Config } from '../../hooks/useRetentionFlowV2Config'
import { FlowBottomArea, FlowContent, FlowOptions } from '../../styles'
import { StyledFlowReasons as S } from './FlowReasons.styles'
import { FLOW_REASON_VALUES } from './constants'

export const FlowReasons: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const { appName, copyKey } = useRetentionFlowV2Config()
  const userId = useSelector(selectUUID)
  const isUpgradePurchased = useSelector(selectIsUpgradePurchased)
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const isCompatibleRetentionFlowPaymentMethod =
    useCompatibleRetentionFlowPaymentMethod()

  const isAnsweredRef = useRef(false)
  const {
    isFeedbackPopupShown,
    closeFeedbackPopup,
    cancelSubscription,
    goToCancellation,
    goToPauseOffer,
  } = useCancelSubscription()

  const { isPausedSubscription } = getSubscriptionUIStatus(
    userSubscriptionInfo?.uiStatus,
  )

  const goToNextStep = (answer: UnsubscribeReason) => {
    if (isPausedSubscription || !isCompatibleRetentionFlowPaymentMethod) {
      goToCancellation()
      return
    }

    if (answer === UnsubscribeReason.TECH_ISSUES) {
      goTo({ pathname: PageId.TECHNICAL_ISSUES, search })
      return
    }

    if (answer === UnsubscribeReason.TOO_EXPENSIVE && !isUpgradePurchased) {
      goTo({ pathname: PageId.DISCOUNT_OFFER, search })
      return
    }

    goToPauseOffer()
  }

  const handleAnswer = (answer: UnsubscribeReason) => {
    if (isAnsweredRef.current) return

    isAnsweredRef.current = true
    dispatch(setAnswersAction({ answers: answer, pageId: PageId.REASONS }))
    dispatch(sendUserAnswersAction())

    eventLogger.logQuestion({
      event: Events.UNSUB_FLOW_QUESTION_2_END,
      question: t('retention.notContinueReasons.title', {
        appName: READABLE_APP_NAMES[appName],
        lng: Language.EN,
      }),
      answers: answer,
      userId,
    })

    goToNextStep(answer)
  }

  const handleFeedbackPopupClose = () => {
    isAnsweredRef.current = false
    closeFeedbackPopup()
  }

  return (
    <FlowPageContainer hasNavigation>
      <S.Content>
        <FlowContent>
          <S.Title>{t(`${copyKey}.reasons.title`)}</S.Title>
          <FlowOptions>
            {FLOW_REASON_VALUES[appName]?.map((value) => (
              <FlowOption key={value} onClick={() => handleAnswer(value)}>
                {t(`${copyKey}.reasons.answers.${value}`)}
              </FlowOption>
            ))}
          </FlowOptions>
        </FlowContent>
      </S.Content>
      <FlowBottomArea isFixed>
        <FlowButton
          type="button"
          variant="secondary"
          onClick={() => handleAnswer(UnsubscribeReason.NO_REASON)}
        >
          {t(`${copyKey}.skip`)}
        </FlowButton>
      </FlowBottomArea>
      <FlowFeedbackPopup
        isShown={isFeedbackPopupShown}
        onComplete={cancelSubscription}
        onClose={handleFeedbackPopupClose}
      />
    </FlowPageContainer>
  )
}
